import { COFFRE_ACCENT_STORAGE_KEY, coffreAccentRootProps } from "./coffreAccent.js";
import { COFFRE_LIST_SORT_STORAGE_KEY } from "./coffreListSort.js";
import { COFFRE_GRID_TILE_KEY } from "./coffreGridTileSize.js";

/** Préférence thème Coffre (autonome du hub / pilotage). */
export const COFFRE_THEME_STORAGE_KEY = "coffre_theme";

/** Barre PWA (nouvel onglet hub) — aligné sur les fonds Coffre. */
const COFFRE_PWA_THEME_COLOR = { light: "#f8fafc", dark: "#0b1220" };

function syncCoffrePwaThemeColor(mode) {
  if (typeof document === "undefined") return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  meta.setAttribute("content", COFFRE_PWA_THEME_COLOR[mode === "dark" ? "dark" : "light"]);
}

/** Bleu dossiers / navigation Coffre (light & dark). */
export const COFFRE_FOLDER_BLUE = "#71baff";

/** Icônes UI — suit `data-coffre-accent` via `.coffre-ui-icon`. */
export const COFFRE_UI_ICON = "coffre-ui-icon";

/** Étoile favori (liste, grille, badge icône). */
export const COFFRE_FAVORITE_STAR = "coffre-favorite-star";
export const COFFRE_FAVORITE_BTN = "coffre-favorite-btn";

/** @deprecated Alias — préférer COFFRE_UI_ICON */
export const COFFRE_ICON_ACCENT = COFFRE_UI_ICON;

/** Dossiers sans couleur perso — hérite de `--coffre-folder`. */
export const COFFRE_FOLDER_ICON = "coffre-folder-icon";

/** Navigation dossiers (sidebar, parent, fil d’Ariane, arborescence transfert). */
export const COFFRE_FOLDER_NAV_IDLE = "coffre-folder-nav-idle";
export const COFFRE_FOLDER_NAV_RING = "coffre-folder-nav-ring";
export const COFFRE_FOLDER_NAV_ACTIVE = "coffre-folder-nav-active";
export const COFFRE_FOLDER_NAV_TEXT = "coffre-folder-nav-text";
export const COFFRE_FOLDER_NAV_HOVER = "coffre-folder-nav-hover";

/** Violet Ma GED — repère visuel distinct du bleu Coffre. */
export const COFFRE_GED_VIOLET = "#8b5cf6";
export const COFFRE_GED_NAV_IDLE = "bg-violet-500/12 dark:bg-[#8b5cf6]/18";
export const COFFRE_GED_NAV_RING = "ring-1 ring-violet-500/35 dark:ring-[#ce9bdd]/40";
export const COFFRE_GED_NAV_ACTIVE = `${COFFRE_GED_NAV_IDLE} ${COFFRE_GED_NAV_RING}`;
export const COFFRE_GED_NAV_TEXT = "text-violet-700 dark:text-[#ce9bdd]";
export const COFFRE_GED_NAV_HOVER =
  "hover:bg-violet-500/18 hover:text-violet-700 dark:hover:bg-[#8b5cf6]/24 dark:hover:text-[#ce9bdd]";

/** @deprecated Préférer COFFRE_FOLDER_NAV_* */
export const COFFRE_DARK_NAV_ACTIVE = COFFRE_FOLDER_NAV_ACTIVE;
export const COFFRE_DARK_NAV_TEXT = COFFRE_FOLDER_NAV_TEXT;
export const COFFRE_DARK_NAV_HOVER = COFFRE_FOLDER_NAV_HOVER;

export function readCoffreTheme() {
  try {
    const v = localStorage.getItem(COFFRE_THEME_STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* ignore */
  }
  return "dark";
}

/** Nettoie les classes thème sur html/body (hub parent ou anciennes versions Coffre). */
export function clearCoffreDocumentThemeClasses() {
  if (typeof document === "undefined") return;
  document.documentElement.classList.remove("dark", "light");
  document.documentElement.removeAttribute("data-coffre-theme");
  document.body?.classList.remove("dark", "light");
}

/** Classes + attributs thème racine Coffre (`.coffre-app`). */
export function coffreAppRootThemeAttrs(theme, accent) {
  const mode = theme === "light" ? "light" : "dark";
  const accentProps = coffreAccentRootProps(accent);
  return {
    "data-coffre-theme": mode,
    "data-coffre-accent": accentProps["data-coffre-accent"],
    style: accentProps.style,
    className: mode === "dark" ? "dark" : "",
  };
}

/** Attributs data + variables CSS pour portails (accent + thème). */
export function coffrePortalDataAttrs(theme, accent) {
  const mode = theme === "light" ? "light" : "dark";
  const accentProps = coffreAccentRootProps(accent);
  return {
    "data-coffre-theme": mode,
    "data-coffre-accent": accentProps["data-coffre-accent"],
    style: accentProps.style,
  };
}

/** Portails hors `.coffre-app` (menu contextuel, etc.). */
export function coffrePortalThemeClass(theme) {
  return theme === "dark" ? "coffre-portal dark" : "coffre-portal";
}

/** Synchronise uniquement la barre PWA — le thème visuel est sur `.coffre-app`. */
export function applyCoffreTheme(mode) {
  syncCoffrePwaThemeColor(mode);
}

export function persistCoffreTheme(mode) {
  try {
    localStorage.setItem(COFFRE_THEME_STORAGE_KEY, mode === "dark" ? "dark" : "light");
  } catch {
    /* ignore */
  }
  applyCoffreTheme(mode);
}

/** Clés à conserver lors d’un `CLEAR_CACHE` hub (installSuiteClearCacheListener). */
export const COFFRE_LOCAL_STORAGE_PRESERVE = [
  COFFRE_THEME_STORAGE_KEY,
  COFFRE_ACCENT_STORAGE_KEY,
  "coffre_browse_view",
  "coffre_split_ratio",
  COFFRE_GRID_TILE_KEY,
  "coffre_session_extend",
  COFFRE_LIST_SORT_STORAGE_KEY,
];

/**
 * Attributs HTML pour empêcher Dashlane, 1Password, LastPass, Bitwarden, Proton Pass…
 * de proposer d’enregistrer le mot de passe maître (secret local, hors SSO site).
 * @see https://support.dashlane.com/hc/en-us/articles/4420122792594
 */
export const COFFRE_MASTER_PASSWORD_INPUT_ATTRS = Object.freeze({
  autoComplete: "off",
  spellCheck: false,
  "data-form-type": "other",
  "data-1p-ignore": "true",
  "data-lpignore": "true",
  "data-bwignore": "true",
  "data-protonpass-ignore": "true",
  "data-dashlane-disabled-on-field": "true",
});
